import React from 'react';
import { useI18n } from '../context/I18nContext';
import { Link } from 'react-router-dom';
import { LogoIcon } from '../components/Icons';
import { useSettings } from '../context/SettingsContext';

const TermsOfUse = () => {
  const { settings, getCityState } = useSettings();

  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
              <LogoIcon className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-gray-900">{settings.companyName}</span>
          </Link>
          <Link to="/" className="text-sm text-gray-600 hover:text-gray-900">
            Voltar ao inicio
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('tou.title')}</h1>
        {/* So aparece em ingles: o corpo das clausulas continua em portugues. */}
        {t('legal.notice') && (
          <p className="mb-8 -mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {t('legal.notice')}
          </p>
        )}

        <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 space-y-6">
          <p className="text-gray-600">
            Ultima atualizacao: {new Date().toLocaleDateString('pt-BR')}
          </p>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.1')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Ao acessar e utilizar a plataforma {settings.companyName}, voce concorda em cumprir e estar vinculado
              a estes Termos de Uso. Se voce nao concordar com qualquer parte destes termos, nao
              devera utilizar nossos servicos.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.2')}</h2>
            <p className="text-gray-600 leading-relaxed">
              A {settings.companyName} e uma plataforma de gestao de conversas que permite:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Gerenciamento centralizado de conversas via API do WhatsApp Business.</li>
              <li>Envio de templates de mensagens aprovados pela Meta.</li>
              <li>Organizacao de leads e contatos.</li>
              <li>Gestao de multiplas conexoes e usuarios.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.3')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Para utilizar nossos servicos, voce deve:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Ter pelo menos 18 anos de idade.</li>
              <li>Fornecer informacoes verdadeiras, precisas e completas.</li>
              <li>Manter a confidencialidade de suas credenciais de acesso.</li>
              <li>Ser responsavel por todas as atividades realizadas em sua conta.</li>
              <li>Notificar imediatamente sobre qualquer uso nao autorizado.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.4')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Voce concorda em nao utilizar a plataforma para:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Enviar spam ou mensagens nao solicitadas em massa.</li>
              <li>Violar leis, regulamentos ou direitos de terceiros.</li>
              <li>Transmitir conteudo ilegal, difamatorio, ofensivo ou prejudicial.</li>
              <li>Tentar acessar sistemas ou dados sem autorizacao.</li>
              <li>Interferir no funcionamento da plataforma ou de seus servidores.</li>
              <li>Violar as politicas da Meta/WhatsApp Business API.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.5')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Todo o conteudo da plataforma, incluindo textos, graficos, logotipos, icones, imagens,
              software e codigo, e propriedade da {settings.companyName} ou de seus licenciadores e esta protegido
              por leis de propriedade intelectual. Voce nao pode reproduzir, distribuir, modificar
              ou criar obras derivadas sem autorizacao expressa.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.6')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Alguns recursos podem exigir assinatura paga. Ao contratar um plano:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Voce autoriza a cobranca recorrente conforme o plano escolhido.</li>
              <li>Os precos podem ser alterados com aviso previo de 30 dias.</li>
              <li>Cancelamentos devem ser feitos antes do proximo ciclo de cobranca.</li>
              <li>Reembolsos sao avaliados caso a caso conforme nossa politica.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.7')}</h2>
            <p className="text-gray-600 leading-relaxed">
              A {settings.companyName} nao se responsabiliza por:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Interrupcoes ou falhas nos servicos da API do WhatsApp.</li>
              <li>Perdas ou danos decorrentes do uso ou impossibilidade de uso da plataforma.</li>
              <li>Acoes de terceiros, incluindo hackers ou outras ameacas de seguranca.</li>
              <li>Conteudo enviado pelos usuarios atraves da plataforma.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.8')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Nos esforcamos para manter a plataforma disponivel 24/7, mas nao garantimos
              disponibilidade ininterrupta. Manutencoes programadas serao comunicadas com
              antecedencia sempre que possivel.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.9')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Reservamo-nos o direito de suspender ou encerrar sua conta em caso de:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2">
              <li>Violacao destes Termos de Uso.</li>
              <li>Atividades fraudulentas ou suspeitas.</li>
              <li>Nao pagamento de valores devidos.</li>
              <li>Solicitacao de autoridades competentes.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.10')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Podemos modificar estes Termos de Uso a qualquer momento. Alteracoes significativas
              serao comunicadas por e-mail ou atraves da plataforma. O uso continuado apos as
              alteracoes constitui aceitacao dos novos termos.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.11')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Estes Termos sao regidos pelas leis da Republica Federativa do Brasil. Qualquer
              disputa sera resolvida nos tribunais da comarca de {settings.city || 'sua cidade'}/{settings.state || 'SP'}.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-semibold text-gray-900">{t('tou.12')}</h2>
            <p className="text-gray-600 leading-relaxed">
              Para duvidas sobre estes Termos de Uso, entre em contato:
            </p>
            <div className="bg-gray-50 rounded-xl p-4 text-gray-600">
              <p><strong>{settings.companyName}</strong></p>
              {settings.cnpj && <p>CNPJ: {settings.cnpj}</p>}
              {settings.email && <p>E-mail: {settings.email}</p>}
              {settings.phone && <p>Telefone: {settings.phone}</p>}
              {settings.address && (
                <p>Endereco: {settings.address}{getCityState() ? ` - ${getCityState()}` : ''}{settings.zipCode ? ` - CEP ${settings.zipCode}` : ''}</p>
              )}
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} {settings.companyName}. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
};

export default TermsOfUse;
